<script setup>
import { ref, computed, onMounted, defineAsyncComponent } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
/***/
import { useAuthUser } from '@compositions/Auth';
import { useTaskConfiguration } from '@compositions/Task/Configuration/TaskConfiguration';
/***/
import { useApplicationStore } from '@stores/application';
import { useAuthUserStore } from '@stores/auth';
import { useAuthCustomerTaskStore } from '@stores/auth/customerTask';
import { useAuthCompletedTaskStore } from '@stores/auth/completedTask';
import { useAuthReturnTaskStore } from '@stores/auth/returnTask';
import { useTaskConfigurationStore } from '@stores/taskConfiguration';

// Composants
const Loader = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/UI/Loader'));
const Layout = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Layouts/Authenticated'));
const Wrapper = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Me/Task/Edit/Wrapper'));

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

// Identifiant public de la tâche
const taskPublicId = route.params.taskPublicId;

// Store
const authUserStore = useAuthUserStore();
const applicationStore = useApplicationStore();
const customerTaskStore = useAuthCustomerTaskStore();
const completedTaskStore = useAuthCompletedTaskStore();
const returnTaskStore = useAuthReturnTaskStore();
const taskConfigurationStore = useTaskConfigurationStore();

// Retourne l'application courante
const currentApplication = computed(() => applicationStore.getCurrent);

// Retourne la tâche correspondant à l'identifiant public dans l'URL
const currentTask = computed(() => {
    return customerTaskStore.getCustomerTaskByPublicId(taskPublicId);
});

// Composition pour l'utilisateur connecté
const { authUser, authUserHasPermission } = useAuthUser({});

// Composition pour le chargement des configurations de tâche
const {
    loadingTaskConfigurations,
    loadingTaskConfigurationsError,
    loadTaskConfiguration,
    loadingTaskFormats,
    loadingTaskFormatsError,
    loadTaskFormats,
} = useTaskConfiguration({});

// Vrai si l'utilisateur est autorisée à accéder à la page
const allowed = ref(false);

// Chargement de la tâche
const loadingTask = ref(true);
const loadingTaskError = ref(false);

// Vrai si des ressources sont en cours de chargement
const loadingResources = computed(() => {
    return (
        loadingTaskConfigurations.value ||
        loadingTaskFormats.value ||
        loadingTask.value
    );
});

// Vrai s'il y a eu des erreurs
const loadingError = computed(() => {
    return (
        loadingTaskConfigurationsError.value ||
        loadingTaskFormatsError.value ||
        loadingTaskError.value
    );
});

// Chargement de la tâche
const loadTask = (() => {
    customerTaskStore.loadCustomerTaskByPublicId(taskPublicId).then(() => {

        const canAccess = (currentTask.value !== null && (
            currentTask.value.canEdit ||
            currentTask.value.canEditAISuggestion ||
            currentTask.value.canEditCustomization ||
            currentTask.value.canCancelPublication ||
            currentTask.value.canConsultProposalsAccepted
        ));

        if(! canAccess) {
            return Promise.reject();
        }

        loadCompleteData().then(() => {
            onTaskLoaded();
        }).catch(() => {
            return Promise.reject();
        })
    }).catch((e) => {
        loadingTaskError.value = true;
        loadingTask.value = false;
    });
});

// Chargement de données additionnelles pour la tâche
const loadCompleteData = (() => {

    const completedStatuses = [ 'COMPLETED', 'PUBLISHED', 'PUBLICATION_WAITING' ];
    const returnStatuses = [ 'RETURN', 'RETURN_WAITING' ];

    // Chargements des propositions pour une tâche complète
    if(completedStatuses.indexOf(currentTask.value.status) !== -1) {
        return completedTaskStore.loadCompletedTaskByPublicId(currentTask.value.publicId, true);
    } else if(returnStatuses.indexOf(currentTask.value.status) !== -1) {
        return returnTaskStore.loadReturnTaskByPublicId(currentTask.value.publicId, true);
    }

    return Promise.resolve();
});

// Méthode appelée après le chargement de la tâche
const onTaskLoaded = (() => {
    loadingTask.value = false;
    allowed.value = true;
    document.title = t('page.me.task.edit.customer.title', {
        task: currentTask.value.title
    }) + ' - ' + currentApplication.value.name;

    customerTaskStore.setTaskView(currentTask.value);
});

// Classes pour la div.layout
const layoutClasses = computed(() => {
    if(! allowed.value) {
        return [];
    }
    return [ 'layout-edit-task' ];
});

// Vrai si le footer est affiché sur la page
const withFooter = ref(false);

// Classes pour la div.wrapper
const wrapperContentClasses = computed(() => {
    const classes = [
        'wrapper-content-task-edit'
    ];

    if(withFooter.value) {
        classes.push('with-footer-bottom');
    }

    return classes;
});

// Chargement des ressources
const loadResources = (() => {
    authUserStore.loadCreditSubscription();
    loadTaskConfiguration();
    loadTaskFormats();
    loadTask();
});

onMounted(() => {
    const canConsult = (authUser.value.type === 'CUSTOMER' && authUserHasPermission('edit task'));
    if(! canConsult) {
        router.push({
            name: 'error',
            params: {
                statusCode: 404
            }
        });
    } else {
        loadResources();
    }
});
</script>

<template>
    <loader v-if="loadingResources"></loader>
    <layout
        :layout-additional-classes="layoutClasses"
        :wrapper-content-classes="wrapperContentClasses.join(' ')"
    >
        <p
            v-if="loadingError"
            class="generic-message generic-message-error"
        >
            {{ $t('page.me.task.edit.customer.loadingError') }}
        </p>
        <wrapper
            v-else-if="currentTask !== null"
            :task="currentTask"
            v-on:with-footer="withFooter = $event"
        ></wrapper>
    </layout>
</template>
